<template>
  <div class="container">
    <div class="row">
      <div class="col-md-8 col-md-offset-2">
        <div class="bsd-section">
          <breadcrumb />
        </div>

        <div class="bsd-section">
          <alert-box ref="alertBox" class="spacer-l" />

          <template v-if="error">
            <router-link
              tag="button"
              :to="{ name: 'panel'}"
              class="z-button z-button--secondary z-button--block"
            >
              Voltar para página inicial
            </router-link>
          </template>
        </div>

        <pix-loader v-if="loading" />

        <pix-generator
          v-else-if="!error"
          from="paymentPromise"
          :title="`Pagar ${periodName} com pix`"
          :title-on-ready="`Copie o QR Code para pagar o ${periodName}`"
          :payment="paymentPromise"
          :ready="pixReady"
          :waiting="pixWaiting"
          :unavailable="pixUnavailable"
          :callback-clipboard="copySuccess"
          :handle-generate-pix="createContractDischargeRequest"
          :has-error="error"
        >
          <contract-full-amount />
        </pix-generator>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import AlertBox from 'components/zilla/alert-box';
import PixLoader from 'content-loaders/pix/pix-loader';
import PixGenerator from 'components/pix/pix-generator';
import Breadcrumb from 'components/breadcrumb';
import ContractFullAmount from 'pages/contract-payment/contract-full-amount';
import ContractFullPaymentService from 'services/ContractFullPaymentService';

export default {
  components: {
    AlertBox,
    PixLoader,
    PixGenerator,
    Breadcrumb,
    ContractFullAmount,
  },
  data: () => ({
    bill: {},
    paymentPromise: {},
    error: false,
    loading: true,
    pixUnavailable: false,
    pixWaiting: false,
    pixReady: false,
    hasBoleto: false,
  }),
  beforeMount() {
    if (!this.showContractDischarge) {
      this.$router.push({ name: 'panel' });
    }
  },
  computed: {
    ...mapState(['currentContract', 'currentBilling']),
    ...mapGetters(['showContractDischarge']),
    periodName() {
      return this.currentContract.period_name;
    },
    contractId() {
      return this.currentContract.id;
    },
  },
  methods: {
    loadExistingContractDischargeRequest() {
      ContractFullPaymentService.getExistingPaymentPromise(this.contractId)
        .then(({ request }) => {
          this.verifyAndGetExistingPixPaymentPromise(request);
        })
        .catch(() => {
          this.showErrorMessage();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    verifyAndGetExistingPixPaymentPromise(request) {
      if (request === null
        || request.status !== 'pending'
        || (!request.pix_payment_promise && request.boleto_payment_promise)
      ) {
        this.pixUnavailable = true;
        this.paymentPromise = null;

        if (request?.boleto_payment_promise) {
          this.alreadyHasBoleto();
        }
      } else if (!request.pix_payment_promise) {
        this.pixWaiting = true;
      } else {
        this.paymentPromise = request.pix_payment_promise;
        this.validatePaymentPromise();
      }
    },
    createContractDischargeRequest() {
      this.pixWaiting = true;
      this.pixUnavailable = false;

      this.setLoadingTimeout();

      if (this.hasBoleto) {
        this.$refs.alertBox.hide();
      }

      return ContractFullPaymentService.charge('pix', this.contractId)
        .then(({ request }) => {
          this.paymentPromise = request.pix_payment_promise;
          this.validatePaymentPromise();
          this.$tracker.logEvent('[contract discharge] Clicked "Gerar Pix"', { request });
        })
        .catch(() => {
          this.showErrorMessage();
        })
        .finally(() => this.$pageLoader.hide());
    },
    validatePaymentPromise() {
      this.resetPixStatus();

      if (this.paymentPromise === null) {
        this.pixUnavailable = true;
      } else if (this.paymentPromise.metadata.qr_code === null) {
        this.pixWaiting = true;

        this.setLoadingTimeout();
      } else {
        this.pixReady = true;
      }
    },
    copySuccess() {
      this.$tracker.logEvent('[contract discharge] Clicked "Copiar o código do pix"');
    },
    setLoadingTimeout() {
      setTimeout(() => {
        if (this.pixWaiting && !this.$refs.alertBox.visible) {
          this.$refs.alertBox.show(
            'warning', 'O Pix está sendo processado',
            'Isso pode demorar alguns minutos, por favor volte mais tarde.',
          );
        }
      }, 10000);
    },
    showErrorMessage() {
      this.error = true;

      this.$refs.alertBox.show(
        'danger', 'Erro inesperado',
        'Não foi possível gerar o pix. Tente novamente mais tarde ou entre em contato com o Quero Pago.',
      );
    },
    resetPixStatus() {
      this.pixUnavailable = false;
      this.pixWaiting = false;
      this.pixReady = false;
    },
    alreadyHasBoleto() {
      this.hasBoleto = true;

      this.$refs.alertBox.show(
        'warning', 'Boleto emitido',
        'Já foi gerado um boleto para a quitação deste contrato. '
        + 'Se optar por realizar o pagamento via PIX, o boleto gerado será invalidado.',
        13000,
      );
    },
  },
  mounted() {
    this.loadExistingContractDischargeRequest();
    this.$tracker.logEvent('[contract discharge] Viewed Pix Page', { contractId: this.contractId });
  },
};
</script>
